import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateRole } from '@/hooks/mutations/roles'
import { toast } from "sonner";
import { usePermissions } from '@/hooks/queries/usePermissions'
import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const CreateRole = () => {
  const navigate = useNavigate()
  const createRole = useCreateRole()
  const { data: permissions, isLoading } = usePermissions()
  const [formData, setFormData] = useState({
    title: '',
    permissions: []
  })
  const [selectAll, setSelectAll] = useState(false);

  const validationSchema = Yup.object({
    title: Yup.string().required('Role title is required'),
    permissions: Yup.array().min(1, 'At least one permission is required').required('Permissions are required')
  })

  const handleSubmit = async (values) => {
    try {
      await createRole.mutateAsync(values)
      toast.success("Role created successfully")
      navigate('/roles')
    } catch (error) {
      toast.error(error.message || 'Failed to create role')
    }
  }

  const handlePermissionChange = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter(id => id !== permissionId)
        : [...prev.permissions, permissionId]
    }))
  }

  const handleSelectAll = (setFieldValue) => {
    if (selectAll) {
      setFieldValue("permissions", []);
    } else {
      setFieldValue("permissions", permissions?.data.map(permission => permission.id));
    }
    setSelectAll(!selectAll);
  };
  

  if (isLoading) return <div className="fixed inset-0 flex items-center justify-center">
  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
</div>

  return (
    <section className='bg-gray-2 dark:bg-dark py-5'>
      <div className='container mx-auto'>
        <div className='flex flex-wrap -mx-4'>
          <div className='w-full px-4'>
            <div className='relative mx-auto max-w-[800px] overflow-hidden rounded-lg bg-white dark:bg-dark-2 shadow-card'>
              <div className='p-8 sm:p-12'>
                <h2 className='mb-6 text-2xl font-bold text-black dark:text-white'>
                  Create New Role
                </h2>
                <Formik
                  initialValues={formData}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, values, errors, touched }) => (
                    <Form>
                      <div className='mb-6'>
                        <label className='mb-3 block text-sm font-medium text-dark dark:text-white'>
                          Role Title
                        </label>
                        <Field
                          type='text'
                          name='title'
                          placeholder='Enter role title'
                          className='w-full rounded-md border border-stroke dark:border-dark-3 bg-transparent py-3 px-6 text-base text-body-color dark:text-dark-6 placeholder:text-body-color outline-none transition focus:border-primary focus-visible:shadow-none'
                        />
                        <ErrorMessage name="title" component="div" className="text-red-600 text-sm" />
                      </div>

                      <div className='mb-6'>
                        <label className='mb-3 block text-sm font-medium text-dark dark:text-white'>
                          Permissions
                        </label>
                        <button
                          type="button"
                          onClick={() => handleSelectAll(setFieldValue)}
                          className='mb-4 rounded-md bg-[#F16745] py-2 px-4 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-opacity-80'
                        >
                          {selectAll ? "Deselect All" : "Select All"}
                        </button>

                        <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                          {permissions?.data?.map((permission) => (
                            <div key={permission.id} className='flex items-center'>
                             <input
  type='checkbox'
  id={`permission-${permission.id}`}
  checked={values.permissions.includes(permission.id)}
  onChange={() => {
    const newPermissions = values.permissions.includes(permission.id)
      ? values.permissions.filter(id => id !== permission.id)
      : [...values.permissions, permission.id];

    setFieldValue("permissions", newPermissions);
    
    // Update Select All state
    setSelectAll(newPermissions.length === permissions?.data?.length);
  }}
  className='mr-2'
/>

                              <label 
                                htmlFor={`permission-${permission.id}`}
                                className='text-sm text-body-color dark:text-dark-6'
                              >
                                {permission.title}
                              </label>
                            </div>
                          ))}
                        </div>
                        <ErrorMessage name="permissions" component="div" className="text-red-600 text-sm" />
                      </div>

                      <div className='flex gap-4'>
                        <button
                          type='submit'
                          className='rounded-md bg-[#F16745] py-3 px-9 text-base font-medium text-white transition duration-300 ease-in-out hover:bg-opacity-80'
                        >
                          Create Role
                        </button>
                        <button
                          type='button'
                          onClick={() => navigate('/roles')}
                          className='rounded-md bg-gray-2 dark:bg-dark-3 py-3 px-9 text-base font-medium text-body-color dark:text-dark-6 transition duration-300 ease-in-out hover:bg-opacity-80'
                        >
                          Cancel
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default CreateRole
